/**
 * @fileoverview Who's here: the class session's register as a dialog off the
 * recorder's attendance row (iOS review B6).
 *
 * The register itself is the existing `ClassSessionAttendance` form, saved
 * on its own button, because who is attending gates what follows: only
 * attending students are in the lesson, its consent check and its
 * recipients. It used to sit inline in the recorder's select column; now the
 * row says "3 of 4 here · 1 needs consent" and this dialog opens on it. The
 * web twin of `mobile/src/components/AttendanceSheet.js`.
 */
import ClassSessionAttendance from "@/components/ClassSessionAttendance";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * The roll in one line, and who still needs consent, for the session row.
 *
 * @param {Object|null} selection - The scheduler selection with `classSession.attendance`.
 * @param {Array} students - The roster, for consent states.
 * @returns {{here: number, total: number, waiting: string[], line: string}|null}
 */
export function attendanceSummary(selection, students) {
  const people = selection?.classSession?.attendance;
  if (!Array.isArray(people) || people.length === 0) return null;
  const attending = people.filter((p) => p.status !== "absent" && p.status !== "excused");
  const waiting = attending
    .map((p) => students.find((s) => s.student_id === p.id))
    .filter((s) => s && s.consent_status !== "granted")
    .map((s) => s.first_name || s.name || "A student");
  const parts = [`${attending.length} of ${people.length} here`];
  if (waiting.length) parts.push(`${waiting.length} need${waiting.length === 1 ? "s" : ""} consent`);
  return { here: attending.length, total: people.length, waiting, line: parts.join(" · ") };
}

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {Object|null} props.selection
 * @param {(selection: Object) => void} props.onChange
 * @param {Array} props.students
 * @param {boolean} [props.disabled]
 * @param {string|null} [props.orgName] - Who requests consent for a program roster.
 */
export function RecordingAttendanceDialog({ open, onOpenChange, selection, onChange, students, disabled = false, orgName = null }) {
  if (!selection?.classSession) return null;
  const summary = attendanceSummary(selection, students);
  const waiting = summary?.waiting || [];
  const names = waiting.join(", ");
  const footer = waiting.length
    ? `Record opens once everyone marked here has consent. Mark ${names} absent, or ask ${orgName || "the organization"} to request it.`
    : "Record opens once everyone marked here has consent. Everyone here has it.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] overflow-y-auto bg-[var(--studio-overlay)] border-[var(--studio-divider-strong)] text-[var(--text-primary)]"
        data-testid="attendance-dialog"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="font-serif text-xl font-normal text-[var(--text-primary)]">Who’s here?</DialogTitle>
          <DialogDescription className="sr-only">Mark who is attending this session.</DialogDescription>
        </DialogHeader>
        <ClassSessionAttendance selection={selection} onChange={onChange} disabled={disabled} />
        <p className="text-[13px] leading-5 text-[var(--text-tertiary)]" data-testid="attendance-dialog-footer">{footer}</p>
        <Button
          onClick={() => onOpenChange(false)}
          className="h-11 w-full rounded-full bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium"
          data-testid="attendance-dialog-done"
        >
          Done
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default RecordingAttendanceDialog;
